import { useFolders, useServices } from "@hq/client-react";
import type { PaneComponentProps } from "@hq/layout";
import { NoteList, TagFilterBar, useNoteViewMode } from "@hq/notes-ui";
import { IconButton, Toggle, ToggleGroup, Tooltip } from "@z/ui";
import { FilePlus, LayoutGrid, List } from "lucide-react";
import { useEffect } from "react";
import { useWorkspace } from "../workspace/WorkspaceContext";

/** Every note, or one folder's notes (`folderId` param), newest first with search and tag filter. */
export function AllNotesPane({ pane }: PaneComponentProps) {
  const ws = useWorkspace();
  const services = useServices();
  const [viewMode, setViewMode] = useNoteViewMode();
  const folderId = typeof pane.params.folderId === "string" ? pane.params.folderId : null;
  const selectedNoteId =
    typeof pane.params.selectedNoteId === "string" ? pane.params.selectedNoteId : null;
  const tag = typeof pane.params.tag === "string" ? pane.params.tag : undefined;
  const { data: folders } = useFolders();
  const folderName = folderId ? folders?.find((f) => f.id === folderId)?.name : undefined;
  useEffect(() => {
    if (folderName !== undefined && folderName !== pane.title) ws.setTitle(pane.id, folderName);
  }, [folderName, pane.id, pane.title, ws]);

  const openNote = (noteId: string, pinned = false) => {
    ws.setParams(pane.id, { ...pane.params, selectedNoteId: noteId });
    ws.openNote(noteId, pinned ? { pinned: true, autoFocus: true } : {});
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 px-6 py-5">
        <h1 className="truncate text-xl font-semibold tracking-tight">
          {folderName ?? "All Notes"}
        </h1>
        <div className="flex items-center gap-2">
          <ToggleGroup
            aria-label="Notes view"
            value={[viewMode]}
            onValueChange={(values) => {
              const next = values[0];
              if (next === "list" || next === "gallery") setViewMode(next);
            }}
            className="border border-border bg-surface p-0.5"
          >
            <Toggle value="list" size="sm" aria-label="List view">
              <List />
              <span>List</span>
            </Toggle>
            <Toggle value="gallery" size="sm" aria-label="Gallery view">
              <LayoutGrid />
              <span>Gallery</span>
            </Toggle>
          </ToggleGroup>
          <Tooltip content="New note" shortcutId="notes.new">
            <IconButton
              label="New note"
              size="sm"
              onClick={() =>
                void services.notes
                  .create(folderId ? { folderId } : {})
                  .then((n) => openNote(n.id, true))
              }
            >
              <FilePlus className="size-4" />
            </IconButton>
          </Tooltip>
        </div>
      </div>
      <TagFilterBar
        value={tag ?? null}
        onChange={(next) => ws.setParams(pane.id, { ...pane.params, tag: next ?? undefined })}
        className="border-b border-border px-3 py-1"
      />
      <NoteList
        viewMode={viewMode}
        showCreate={false}
        folderId={folderId}
        includeSubfolders
        {...(tag ? { tag } : {})}
        selectedNoteId={selectedNoteId}
        onSelect={(id) => openNote(id)}
        className="min-h-0 flex-1 overflow-y-auto"
      />
    </div>
  );
}
